import { Controller } from "@hotwired/stimulus"
import { coastNumber, fireNumber, monthsToCoast, monthsToTarget, project, realReturn } from "../fire_math"
import { track } from "./analytics_controller"
import { currencyFromQuery, drawChart, duration, line, money, monthYear, nextCurrency, percent, renderLevers, renderSteps, saveQuery, yearLabels } from "../fire_view"

export default class extends Controller {
  static targets = ["currency", "form", "advanced", "real", "number", "multiple", "duration", "date", "progress", "bar", "canvas", "milestones", "levers", "milestoneTemplate", "leverTemplate"]

  connect() {
    this.currency = currencyFromQuery()
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(false)
  }

  cycle() {
    this.currency = nextCurrency(this.currency)
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(true)
  }

  money(value) {
    return money(value, this.currency)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.chart?.destroy()
  }

  submit(event) {
    event.preventDefault()
  }

  change() {
    this.update(true)
  }

  read(name) {
    const value = parseFloat(this.formTarget.elements[name].value)
    return Number.isFinite(value) ? value : null
  }

  inputs() {
    const age = Math.max(0, this.read("age") || 0)
    return {
      age,
      retire: Math.max(0, this.read("retire") || 0),
      spending: Math.max(0, this.read("spending") || 0),
      portfolio: Math.max(0, this.read("portfolio") || 0),
      monthly: Math.max(0, this.read("monthly") || 0),
      nominal: (this.read("return") || 0) / 100,
      withdrawal: (this.read("withdrawal") || 0) / 100,
      inflation: (this.read("inflation") || 0) / 100,
      fees: (this.read("fees") || 0) / 100,
      tax: Math.min(0.99, Math.max(0, (this.read("tax") || 0) / 100))
    }
  }

  update(record) {
    const inputs = this.inputs()
    const annual = realReturn(inputs)
    const target = fireNumber(inputs.spending, inputs.withdrawal, inputs.tax)
    const total = Math.round((inputs.retire - inputs.age) * 12)
    const plan = { portfolio: inputs.portfolio, monthly: inputs.monthly, annual }
    const coast = target > 0 && total > 0 ? coastNumber(target, annual, total) : 0
    const months = coast > 0 ? monthsToCoast({ ...plan, target, months: total }) : null
    const context = { inputs, annual, target, total, plan, coast, months }

    this.realTarget.textContent = `About ${percent(annual)} a year after inflation${inputs.fees > 0 ? " and fees" : ""}.`
    this.renderSummary(context)
    this.renderChart(context)
    this.renderMilestones(context)
    this.renderLevers(context)

    if (record) this.record(months, inputs.monthly)
  }

  record(months, monthly) {
    saveQuery(this.formTarget, this.currency)

    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      let years = months === null ? "never" : Math.floor(months / 12)
      if (months === 0) years = "now"
      else if (monthly === 0) years = "no-saving"
      track("coast-fire-run", { currency: this.currency.code, years, advanced: this.advancedTarget.open })
    }, 1500)
  }

  ageAt(months) {
    return Math.floor(this.inputs().age + months / 12)
  }

  path({ plan, total, months }) {
    const horizon = Math.max(12, total)
    if (months === null || months >= horizon) return project({ ...plan, months: horizon })

    const saving = project({ ...plan, months })
    const coasting = project({ ...plan, portfolio: saving[months].value, monthly: 0, months: horizon - months })
    return [...saving, ...coasting.slice(1).map((point) => ({ ...point, month: point.month + months, invested: saving[months].invested }))]
  }

  renderSummary({ inputs, target, total, coast, months }) {
    const progress = coast > 0 ? Math.min(1, inputs.portfolio / coast) : 0

    this.numberTarget.textContent = coast > 0 ? this.money(coast) : "—"
    if (total <= 0) this.multipleTarget.textContent = "Set a retirement age above your age"
    else if (target === 0) this.multipleTarget.textContent = "Add your spending and a withdrawal rate"
    else this.multipleTarget.textContent = `Grows into ${this.money(target)} by ${inputs.retire} on its own`

    this.progressTarget.textContent = `${Math.round(progress * 100)}%`
    this.barTarget.style.width = `${progress * 100}%`

    if (coast === 0) {
      this.durationTarget.textContent = "—"
      this.dateTarget.textContent = ""
    } else if (months === 0) {
      this.durationTarget.textContent = "Now"
      this.dateTarget.textContent = "You've reached Coast FIRE. What you have grows into your FIRE number by itself"
    } else if (inputs.monthly === 0) {
      this.durationTarget.textContent = "—"
      this.dateTarget.textContent = `You're ${this.money(coast - inputs.portfolio)} short. Add a monthly saving to see when you could stop`
    } else if (months === null) {
      this.durationTarget.textContent = `Not before ${inputs.retire}`
      this.dateTarget.textContent = "At this pace you'd need to save until you retire"
    } else {
      this.durationTarget.textContent = String(this.ageAt(months))
      this.dateTarget.textContent = `Around ${monthYear(months)}, in ${duration(months)}`
    }
  }

  renderChart(context) {
    const { target, total, annual } = context
    const points = this.path(context)
    const horizon = Math.max(1, Math.ceil(points.length / 12))
    const yearly = points.filter((point) => point.month % 12 === 0)

    this.chart = drawChart(this.chart, this.canvasTarget, {
      labels: yearLabels(points),
      horizon,
      currency: this.currency,
      datasets: [
        line("Portfolio", yearly.map((point) => point.value), "#00d6b4", { backgroundColor: "rgba(0, 214, 180, 0.08)", fill: "origin", pointHoverRadius: 4 }),
        line("Coast number", yearly.map((point) => coastNumber(target, annual, Math.max(0, total - point.month))), "#1d8cf8"),
        line("FIRE number", yearly.map(() => target), "#e14eca")
      ]
    })
  }

  renderMilestones(context) {
    const { inputs, target, total, plan, coast, months } = context
    const share = coast > 0 ? Math.round(Math.min(1, inputs.portfolio / coast) * 100) : 0
    const rows = [{ kind: "today", when: "Today", title: "Where you are now", detail: coast > 0 ? `${share}% of your Coast FIRE number` : "Fill in the form to see your plan", amount: inputs.portfolio }]

    if (coast > 0) {
      const points = this.path(context)

      if (months > 0) {
        rows.push({ kind: "coast", month: months, title: "Reach Coast FIRE and stop saving", detail: `At ${this.ageAt(months)}. From here it grows on its own`, amount: points[months].value })

        const full = monthsToTarget({ ...plan, target, limit: total })
        if (full !== null && full < total) {
          rows.push({ kind: "milestone", month: full, title: "Full FIRE if you keep saving", detail: `At ${this.ageAt(full)}, if you never stop`, amount: target })
        }
      }

      if (months === null) {
        rows.push({ kind: "fire", month: total, title: "Retirement", detail: `At ${inputs.retire}. You'd still be ${this.money(target - points[total].value)} short`, amount: points[total].value })
      } else {
        rows.push({ kind: "fire", month: total, title: "Retirement", detail: `At ${inputs.retire}, with your FIRE number`, amount: points[total].value })
      }
    }

    renderSteps(this.milestonesTarget, this.milestoneTemplateTarget, rows.sort((first, second) => (first.month || 0) - (second.month || 0)), this.currency)
  }

  renderLevers({ inputs, annual, target, total, plan, months }) {
    if (target === 0 || total <= 0) {
      this.leversTarget.replaceChildren()
      return
    }

    const extraSaving = 250 * this.currency.scale
    const spendingCut = 3000 * this.currency.scale
    const lowerSpending = inputs.spending - spendingCut
    const levers = [
      { label: `Save ${this.money(extraSaving)} more a month`, detail: `${this.money(inputs.monthly + extraSaving)} a month`, months: monthsToCoast({ ...plan, monthly: inputs.monthly + extraSaving, target, months: total }) },
      lowerSpending > 0 && { label: `Spend ${this.money(spendingCut)} less a year in retirement`, detail: `FIRE number drops to ${this.money(fireNumber(lowerSpending, inputs.withdrawal, inputs.tax))}`, months: monthsToCoast({ ...plan, target: fireNumber(lowerSpending, inputs.withdrawal, inputs.tax), months: total }) },
      { label: "Retire 5 years later", detail: `At ${inputs.retire + 5}`, months: monthsToCoast({ ...plan, target, months: total + 60 }) },
      { label: "Earn 1% less a year", detail: `About ${percent(annual - 0.01)} after inflation`, months: monthsToCoast({ ...plan, annual: annual - 0.01, target, months: total }) }
    ].filter(Boolean)

    renderLevers(this.leversTarget, this.leverTemplateTarget, months, levers)
  }
}
